import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { PlatformEnum } from '@panamax/app-state';
import { ExceptionResolution, OrderItem } from '@usf/ngrx-order';
import { ImageVariantEnum } from 'src/app/product-detail/models/image-variant.enum';
import { getFirstImageURL } from 'src/app/product-detail/utils/product-images.util';
import { Product } from 'src/app/shared/models/product.model';
import { ExceptionType } from '../../../../pages/order-exceptions/constants/order-exception-type.enum';
import { ResolutionType } from '../../../../pages/order-exceptions/constants/resolution-type.enum';
import { EmittedFlippedProductNumber } from '../../../../pages/order-exceptions/models/emitted-flipped-product-number.model';
import { EmittedPartialFlag } from '../../../../pages/order-exceptions/models/emitted-partial-flag-selection.model';
import { EmittedResolution } from '../../../../pages/order-exceptions/models/emitted-resolution.model';
import { ProductSummary } from '@usf/product-types';
import { getAlternativeThumbnail } from 'src/app/product-detail/utils/product-images.util';

@Component({
  template: '',
})
export class ExceptionProductBaseComponent implements OnInit, OnChanges {
  @Input() exceptionResolution: ExceptionResolution;
  @Input() exceptionType: ExceptionType;
  @Input() product: Product;
  @Input() isOriginalProduct: boolean;
  @Input() partialReserveFlag: string;
  @Input() isPartialReserve: boolean;
  @Input() originalProduct: Product;
  @Input() banner: boolean;
  @Input() subOrderItem: OrderItem;
  @Input() platformEnum: PlatformEnum;
  @Input() productSummary: ProductSummary;
  @Input() isOffline: boolean;
  @Input() masterListRestricted: boolean;

  @Output() waitForRestockExceptionResolved = new EventEmitter();
  @Output() viewSubstitutesClicked = new EventEmitter();
  @Output() searchForSubsClicked = new EventEmitter();
  @Output() selectSubstituteExceptionResolved = new EventEmitter();
  @Output() removeItemExceptionResolved = new EventEmitter();
  @Output() partialReserveFlagResolved = new EventEmitter<EmittedPartialFlag>();
  @Output() exceptionResolved = new EventEmitter();
  @Output()
  substituteProductFlipped = new EventEmitter<EmittedFlippedProductNumber>();
  @Output() editRowClicked = new EventEmitter<ExceptionResolution>();

  thumbnailImageURL: string;
  isFlipsideVisible: boolean;
  subsVisible = false;
  selectedReserveQuantityOption: string;
  hideProduct = false;
  maxDisplayTags = 2;

  constructor(
    protected modalController: ModalController,
    protected actionSheetController: ActionSheetController,
  ) {}
  exceptionTypes = ExceptionType;

  ngOnInit(): void {
    this.thumbnailImageURL = getFirstImageURL(
      this.product?.summary,
      ImageVariantEnum.Medium,
    );
    this.updateFlags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.product?.currentValue) {
      this.thumbnailImageURL = getFirstImageURL(
        this.product?.summary,
        ImageVariantEnum.Medium,
      );
      this.updateFlags();
    }
    this.hideForMLRestricted();
  }

  public get ResolutionType() {
    return ResolutionType;
  }

  onSubSelected(resolution: EmittedResolution) {
    this.subsVisible = false;
    this.exceptionResolved.emit(resolution);
  }

  getAternativeThumbnail = (thumbnailImageURL: string): string =>
    getAlternativeThumbnail(this.productSummary, thumbnailImageURL);

  onExceptionResolution(resolutionType: ResolutionType) {
    this.subsVisible = false;
    const resolution: EmittedResolution = {
      exceptionResolution: { ...this.exceptionResolution, expanded: false },
      resolutionType,
      subOrderItem: this.subOrderItem,
    };
    this.exceptionResolved.emit(resolution);
  }

  editRow() {
    this.editRowClicked.emit(this.exceptionResolution);
  }

  showFlipSide() {
    this.isFlipsideVisible = !this.isFlipsideVisible;
    this.substituteProductFlipped.emit({
      productNumber: this.isFlipsideVisible
        ? this.product.productNumber
        : undefined,
      exceptionResolution: this.exceptionResolution,
    });
  }

  showSubstitutes() {
    this.subsVisible = true;
    this.viewSubstitutesClicked.emit();
  }

  //only show first sub for ML restricted users
  hideForMLRestricted() {
    this.hideProduct =
      this.masterListRestricted &&
      this.exceptionResolution?.orderException?.subOrReplaceItems[0]
        ?.productNumber !== this.product?.productNumber;
  }

  searchForSubs() {
    this.searchForSubsClicked.emit(this.exceptionResolution);
  }

  confirmPartialUnavailable() {
    if ('send' === this.selectedReserveQuantityOption) {
      if (this.exceptionType === ExceptionType.PRODUCT_UNAVAILABLE) {
        this.onExceptionResolution(ResolutionType.ACKNOWLEDGED);
      } else {
        this.onExceptionResolution(ResolutionType.ADD_TO_WAITLIST);
      }
    } else {
      this.onExceptionResolution(ResolutionType.REMOVE);
    }
  }
  confirmAndSubParitalUnavailable() {
    if (this.exceptionType === ExceptionType.PRODUCT_UNAVAILABLE) {
      this.partialReserveFlagResolved.emit({
        flag: 'R',
        exceptionResolution: this.exceptionResolution,
      });
    } else {
      this.partialReserveFlagResolved.emit({
        flag: 'S',
        exceptionResolution: this.exceptionResolution,
      });
    }
  }

  clearSubstitute() {
    this.partialReserveFlagResolved.emit({
      flag: undefined,
      exceptionResolution: this.exceptionResolution,
    });
  }

  private updateFlags() {
    if (
      this.exceptionResolution?.resolutionType === 'Added to Waitlist' ||
      this.exceptionResolution?.resolutionType === 'Reserved' ||
      !this.exceptionResolution?.resolutionType
    ) {
      this.selectedReserveQuantityOption = 'send';
    } else {
      this.selectedReserveQuantityOption = 'remove';
    }

    this.isFlipsideVisible =
      !!this.exceptionResolution?.flippedProductNumber &&
      this.exceptionResolution?.flippedProductNumber ===
        this.product?.productNumber;
  }
}
